import React, { useState, useMemo, useEffect, memo, useRef, useCallback } from 'react';
import JobCardUnassigned from '../../components/schedule/job-cards/JobCardUnassigned';
import { format, parseISO } from 'date-fns';
import { useQuery } from '@apollo/client';
import { GET_ACTIVE_TEAMS } from '../../graphql/teams';
import AssignmentAvailabilityWarning from '../team-availability/AssignmentAvailabilityWarning';
import { toast } from 'react-hot-toast';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const UnassignedTasks = memo(function UnassignedTasks({
  unassignedJobs = [],
  totalJobsCount = { total: 0, assigned: 0, unassigned: 0 },
  selectedJobs = [],
  setSelectedJobs,
  selectedUserId = '',
  setSelectedUserId,
  filteredUsers = [],
  userFilteringInfo = null,
  handleAssignJobs,
  handleAssignJobsToTeam, // New prop for team assignment
  assignLoading = false,
  setEditingJob,
  isHousekeeperManager = false,
  isPropertyManager = false,
  unassignedJobsContainerRef,
  loadMoreJobs,
  hasMoreJobs = false,
  isLoadingMoreJobs = false,
  startDate,
  endDate,
  // 🎯 NEW: Add loading props
  jobsLoading = false,
  assignedJobsLoading = false,
  usersLoading = false,
  // 🎯 NEW: Bulk date update props
  onBulkDateUpdate,
  bulkDateUpdateLoading = false,
  // 🎯 NEW: Combined bulk update and assign props
  onBulkUpdateAndAssign,
  bulkUpdateAndAssignLoading = false
}) {
  
  // Helper function to format area names: remove underscores and capitalize each word
  const formatAreaName = (area) => {
    if (!area) return '';
    return area
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };
  
  // Helper function to format user display name with role and areas
  const formatUserDisplayName = (user) => {
    let displayName = user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim();
    
    // Add role in parentheses
    let roleDisplay = '';
    if (user.role) {
      switch (user.role) {
        case 'HOUSEKEEPER':
          roleDisplay = 'Housekeeper';
          break;
        case 'ADMIN':
          roleDisplay = 'Admin';
          break;
        case 'HOUSEKEEPER_MANAGER':
          roleDisplay = 'Housekeeper Manager';
          break;
        case 'PROPERTY_MANAGER':
          roleDisplay = 'Property Manager';
          break;
        case 'INSPECTOR':
          roleDisplay = 'Inspector';
          break;
        case 'MAINTENANCE':
          roleDisplay = 'Maintenance';
          break;
        default:
          roleDisplay = user.role;
      }
    }
    
    // Add areas if available
    let areasDisplay = '';
    if (user.areas && Array.isArray(user.areas) && user.areas.length > 0) {
      const formattedAreas = user.areas
        .map(area => formatAreaName(area))
        .join(', ');
      areasDisplay = ` - ${formattedAreas}`;
    } else if (user.role === 'ADMIN') {
      areasDisplay = ' - All Areas';
    }
    
    return `${displayName}${roleDisplay ? ` (${roleDisplay})` : ''}${areasDisplay}`;
  };
  // Default to expanded
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Refs for simple auto-scroll detection
  const containerRef = useRef(null);
  const observerRef = useRef(null);
  const lastLoadTimeRef = useRef(0);
  
  // State for assignment type (user or team)
  const [assignmentType, setAssignmentType] = useState('user'); // 'user' or 'team'
  const [selectedTeamId, setSelectedTeamId] = useState('');
  
  // 🎯 NEW: State for bulk date operations
  const [bulkDateValue, setBulkDateValue] = useState('');
  
  // Help modal state for bulk date functionality
  const [showDateHelpModal, setShowDateHelpModal] = useState(false);
  
  // Load teams data
  const { data: teamsData } = useQuery(GET_ACTIVE_TEAMS, {
    fetchPolicy: 'cache-first', // Use cache first for better performance
    notifyOnNetworkStatusChange: false, // Disable to reduce re-renders
    errorPolicy: 'all' // Show partial data even if there are errors
  });
  const [dateParsingErrors, setDateParsingErrors] = useState([]);

  const toggleAccordion = (e) => {
    // Stop event propagation to prevent it from affecting other components
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };
  
  // 🎯 NEW: Handle bulk date update
  const handleBulkDateUpdate = async () => {
    if (!bulkDateValue || selectedJobs.length === 0 || !onBulkDateUpdate) {
      return;
    }
    
    try {
      await onBulkDateUpdate(selectedJobs, bulkDateValue);
      setBulkDateValue(''); // Clear date field after success
      setSelectedJobs([]); // Clear selection for safety
    } catch (error) {
      // Error handling is done by the parent component
    }
  };
  
  // 🎯 NEW: Determine if we're in initial loading state
  const isInitialLoading = jobsLoading || assignedJobsLoading || usersLoading;
  
  // 🎯 NEW: Check if we have any data loaded
  const hasAnyData = unassignedJobs.length > 0 || totalJobsCount.unassigned > 0;
  
  // 🎯 NEW: Enhanced loading state renderer
  const renderLoadingState = () => {
    // Show initial loading state
    if (isInitialLoading && !hasAnyData) {
      return (
        <div className="p-8 flex flex-col items-center justify-center">
          <div className="flex items-center space-x-3">
            <svg className="animate-spin h-6 w-6 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <div>
              <p className="text-sm font-medium text-gray-700">Loading unassigned jobs...</p>
              <p className="text-xs text-gray-500">Please wait while we fetch your data</p>
            </div>
          </div>
        </div>
      );
    }
    
    // Show progressive loading state
    if (isLoadingMoreJobs) {
      return (
        <div className="p-4 flex justify-center items-center">
          <div className="flex items-center space-x-2">
            <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-sm text-gray-600">Loading more jobs...</span>
          </div>
        </div>
      );
    }
    
    return null;
  };
  
  // Utility function to safely format a date
  const safeFormatDate = (date, pattern = 'EEEE, MMMM d, yyyy') => {
    try {
      if (!date) return 'Unknown Date';
      
      // If it's a timestamp number
      if (typeof date === 'number') {
        return format(new Date(date), pattern);
      }
      
      // If it's a Date object
      if (date instanceof Date) {
        return format(date, pattern);
      }
      
      // If it's an ISO string
      if (typeof date === 'string' && date.includes('T')) {
        return format(new Date(date), pattern);
      }
      
      // If it's a YYYY-MM-DD string
      if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Parse the date with noon time to avoid timezone shifts
        const [year, month, day] = date.split('-').map(Number);
        return format(new Date(year, month - 1, day, 12, 0, 0), pattern);
      }
      
      // Default fallback
      return format(new Date(date), pattern);
    } catch (err) {
      return String(date) || 'Unknown Date';
    }
  };
  
  // Extract date from job in a safe way
  const extractDateKey = (job) => {
    if (!job || !job.scheduledDate) return null;
    
    try {
      let date;
      
      // Handle different possible formats
      if (typeof job.scheduledDate === 'number') {
        date = new Date(job.scheduledDate);
      } else if (job.scheduledDate instanceof Date) {
        date = job.scheduledDate;
      } else if (typeof job.scheduledDate === 'string') {
        if (job.scheduledDate.includes('T')) {
          // ISO format
          date = new Date(job.scheduledDate);
        } else {
          // YYYY-MM-DD format
          const [year, month, day] = job.scheduledDate.split('-').map(Number);
          date = new Date(year, month - 1, day, 12, 0, 0);
        }
      } else {
        return null;
      }
      
      // Return YYYY-MM-DD format as the key
      return date.toISOString().split('T')[0];
    } catch (error) {
      setDateParsingErrors(prev => [...prev, { jobId: job.id, error: error.message }]);
      return null;
    }
  };

  // Availability warning inputs: users being assigned + dates being scheduled.
  const availabilityUserIds = useMemo(() => {
    if (assignmentType === 'user') {
      return selectedUserId ? [selectedUserId] : [];
    }
    if (assignmentType === 'team' && selectedTeamId) {
      const team = teamsData?.activeTeams?.find(
        (candidate) => String(candidate.id) === String(selectedTeamId)
      );
      return Array.isArray(team?.memberIds) ? team.memberIds : [];
    }
    return [];
  }, [assignmentType, selectedUserId, selectedTeamId, teamsData]);

  const availabilityDates = useMemo(() => {
    if (!selectedJobs || selectedJobs.length === 0) return [];
    if (bulkDateValue) return [bulkDateValue];
    if (!Array.isArray(unassignedJobs)) return [];
    const seen = new Set();
    const dates = [];
    selectedJobs.forEach((jobId) => {
      const job = unassignedJobs.find((candidate) => candidate?.id === jobId);
      if (!job?.scheduledDate) return;
      const key = extractDateKey(job);
      if (!key || seen.has(key)) return;
      seen.add(key);
      dates.push(job.scheduledDate);
    });
    return dates;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJobs, bulkDateValue, unassignedJobs.map((j) => j?.id).join(',')]);
  
  // Group unassigned jobs by date for better organization and sort within each date
  const jobsByDate = useMemo(() => {
    const grouped = {};
    
    // Ensure unassignedJobs is an array before proceeding
    if (!Array.isArray(unassignedJobs) || unassignedJobs.length === 0) {
      return grouped;
    }
    
    
    // Group jobs by their scheduled date
    unassignedJobs.forEach(job => {
      // Add null check for job
      if (!job) return;
      
      const dateKey = extractDateKey(job);
      if (!dateKey) return;
      
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      
      grouped[dateKey].push(job);
    });
    
    // Sort the dates and sort jobs within each date by property name
    return Object.keys(grouped)
      .sort()
      .reduce((result, date) => {
        // Sort jobs within this date by property name (alphabetically)
        result[date] = grouped[date].sort((a, b) => {
          const nameA = (a.property?.name || '').toLowerCase();
          const nameB = (b.property?.name || '').toLowerCase();
          
          // Primary sort: property name
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          
          // Secondary sort: property code (if names are identical)
          const codeA = (a.property?.propertyCode || '').toLowerCase();
          const codeB = (b.property?.propertyCode || '').toLowerCase();
          
          if (codeA < codeB) return -1;
          if (codeA > codeB) return 1;
          
          // Tertiary sort: job ID (for consistency)
          return a.id.localeCompare(b.id);
        });
        return result;
      }, {});
  }, [unassignedJobs]);
  
  // Check if we have multiple dates
  const hasMultipleDates = useMemo(() => {
    const dateCount = Object.keys(jobsByDate).length;
    return dateCount > 1;
  }, [jobsByDate]);
  
  // Track date parsing errors (silently)
  useEffect(() => {
    if (dateParsingErrors.length > 0) {
      // Errors are tracked in state for potential future use
    }
  }, [dateParsingErrors]);

  // Store scroll position before loading more jobs
  const scrollBeforeLoadRef = useRef(0);
  
  // Enhanced load more with scroll preservation
  const loadMoreWithScrollPreservation = useCallback(() => {
    if (!containerRef.current) return;
    
    // Store current scroll position
    scrollBeforeLoadRef.current = containerRef.current.scrollTop;
    
    // Call the original load more function
    loadMoreJobs(20);
  }, [loadMoreJobs, unassignedJobs?.length]);

  // Simple auto-scroll detection - minimal implementation
  const handleScroll = useCallback(() => {
    if (!containerRef.current || !hasMoreJobs || isLoadingMoreJobs) {
      return;
    }

    const container = containerRef.current;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    
    // If scrolled to within 50px of bottom (more conservative), load more
    if (scrollHeight - scrollTop - clientHeight < 50) {
      const now = Date.now();
      if (now - lastLoadTimeRef.current > 1000) { // 1 second throttle
        lastLoadTimeRef.current = now;
        loadMoreWithScrollPreservation();
      }
    }
  }, [hasMoreJobs, isLoadingMoreJobs, loadMoreWithScrollPreservation]);

  // Set up scroll listener
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll]);

  // Restore scroll position after jobs are loaded
  useEffect(() => {
    // If we just finished loading more jobs, restore scroll position
    if (!isLoadingMoreJobs && scrollBeforeLoadRef.current > 0) {
      const container = containerRef.current;
      if (container) {
        // Use requestAnimationFrame to ensure DOM has updated
        requestAnimationFrame(() => {
          container.scrollTop = scrollBeforeLoadRef.current;
          scrollBeforeLoadRef.current = 0; // Reset
        });
      }
    }
  }, [isLoadingMoreJobs]);

  // Sort users alphabetically by first name
  const sortedUsers = useMemo(() => {
    if (!Array.isArray(filteredUsers)) return [];
    
    return [...filteredUsers].sort((a, b) => {
      // Try to get firstName if available, otherwise extract from name
      const getFirstName = (user) => {
        if (user.firstName) {
          return user.firstName.toLowerCase();
        }
        // If no firstName property, extract first word from name
        if (user.name) {
          return user.name.split(' ')[0].toLowerCase();
        }
        return '';
      };
      
      const firstNameA = getFirstName(a);
      const firstNameB = getFirstName(b);
      
      return firstNameA.localeCompare(firstNameB);
    });
  }, [filteredUsers]);

  // 🎯 ENHANCED: Helper function to show message when no unassigned jobs are found
  const renderEmptyState = () => {
    // Don't show empty state while loading
    if (isInitialLoading || isLoadingMoreJobs) return null;
    
    // Don't show empty state if we have jobs
    if (Object.keys(jobsByDate).length > 0) return null;
    
    return (
      <div className="p-6 text-center">
        {totalJobsCount.unassigned > 0 ? (
          <>
            <p className="text-gray-600 mb-3 font-medium">
              There are {totalJobsCount.unassigned} unassigned jobs available
            </p>
            {hasMoreJobs && (
              <button 
                onClick={() => loadMoreJobs(15)}
                className="mt-2 px-5 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 font-medium shadow-sm transition-colors"
              >
                Load Next 15 Unassigned Jobs
              </button>
            )}
          </>
        ) : (
          <div>
            <p className="text-gray-500 mb-3">No unassigned jobs currently visible.</p>
            {hasMoreJobs && (
              <p className="text-sm text-gray-400">Loading more jobs automatically...</p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div 
        className="p-4 border-b flex items-center justify-between cursor-pointer hover:bg-gray-50"
        onClick={toggleAccordion}
      >
        <div className="flex items-center">
          <h2 className="text-xl font-semibold">Unassigned</h2>
          <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
            {totalJobsCount.unassigned || 0}
          </span>
        </div>
        <div className="text-gray-500">
          {isExpanded ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </div>
      </div>
      
      {isExpanded && (
        <div className="p-2 md:p-4">
          {/* 🎯 NEW: Show loading state first if initial loading */}
          {isInitialLoading && !hasAnyData ? (
            renderLoadingState()
          ) : (
            <>
              {/* Mobile-friendly assignment controls */}
              <div className="mb-4 space-y-3 md:sticky md:top-0 md:z-50 md:bg-white md:border-b md:border-gray-200 md:pt-4 md:pb-4 md:mb-4 md:-mx-4 md:px-4">
                {/* Assignment Type Toggle */}
                <div className="flex rounded-md border border-gray-300">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAssignmentType('user');
                      setSelectedTeamId('');
                    }}
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-l-md transition-colors ${
                      assignmentType === 'user'
                        ? 'bg-sky-600 text-white border-sky-600'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Assign to Individual
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAssignmentType('team');
                      setSelectedUserId('');
                    }}
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-r-md transition-colors ${
                      assignmentType === 'team'
                        ? 'bg-sky-600 text-white border-sky-600'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Assign to Team
                  </button>
                </div>

                {/* Enhanced Action Controls - Combined Workflow */}
                <div className="space-y-3">
                  {/* Assignment and Date Selection Row */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  {assignmentType === 'user' ? (
                    <select
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      className="border rounded-md p-2 w-full sm:w-auto sm:min-w-[250px] bg-white"
                      aria-label={isHousekeeperManager ? "Select housekeeper" : "Select employee"}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <option value="">{isHousekeeperManager ? "Select Housekeeper..." : "Select Employee..."}</option>
                      {sortedUsers.map(user => (
                        <option key={user.id} value={user.id}>{formatUserDisplayName(user)}</option>
                      ))}
                    </select>
                  ) : (
                    <select
                      value={selectedTeamId}
                      onChange={(e) => setSelectedTeamId(e.target.value)}
                      className="border rounded-md p-2 w-full sm:w-auto sm:min-w-[250px] bg-white"
                      aria-label="Select team"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <option value="">Select Team...</option>
                      {teamsData?.activeTeams?.map(team => (
                        <option key={team.id} value={team.id}>
                          {team.name} ({team.memberCount} member{team.memberCount !== 1 ? 's' : ''})
                        </option>
                      ))}
                    </select>
                  )}
                    
                    {/* Date Selection Input with Help Icon */}
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        value={bulkDateValue}
                        onChange={(e) => setBulkDateValue(e.target.value)}
                        className="border rounded-md p-2 w-full sm:w-auto bg-white text-gray-900"
                        aria-label="Select date for jobs (optional)"
                        onClick={(e) => e.stopPropagation()}
                        title="Select a new date for the jobs"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDateHelpModal(true);
                        }}
                        className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                        aria-label="Help with bulk date changes"
                        title="Learn how bulk date changes work"
                      >
                        <HelpOutlineIcon sx={{ fontSize: 16 }} />
                      </button>
                    </div>
                  </div>

                  <AssignmentAvailabilityWarning
                    userIds={availabilityUserIds}
                    dates={availabilityDates}
                    variant="full"
                    subtitle={
                      bulkDateValue
                        ? 'One or more assignees are flagged as unavailable on the new bulk date below.'
                        : 'One or more assignees are flagged as unavailable on the selected jobs\u2019 dates.'
                    }
                  />

                  {/* Combined Action Button */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // Use combined operation only if date is specified OR if both assignment and date are involved
                        // Otherwise, use direct assignment for better performance and clarity
                        if (onBulkUpdateAndAssign && bulkDateValue) {
                          // Use combined operation when date is specified
                          onBulkUpdateAndAssign(
                            selectedJobs, 
                            bulkDateValue || null,
                            assignmentType === 'user' ? selectedUserId : null,
                            assignmentType === 'team' ? selectedTeamId : null
                          );
                        } else if (assignmentType === 'user') {
                          // Direct individual assignment
                          handleAssignJobs();
                        } else if (assignmentType === 'team') {
                          // Direct team assignment
                          if (handleAssignJobsToTeam && selectedTeamId) {
                            handleAssignJobsToTeam(selectedJobs, selectedTeamId);
                          } else {
                            toast.error('Please select a team');
                          }
                        }
                      }}
                      disabled={
                        selectedJobs.length === 0 || 
                        (assignLoading || bulkUpdateAndAssignLoading) || 
                        (assignmentType === 'user' && !selectedUserId) ||
                        (assignmentType === 'team' && !selectedTeamId)
                      }
                      className="flex-1 px-4 py-2 bg-green-600 text-white whitespace-nowrap rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {(assignLoading || bulkUpdateAndAssignLoading) ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Processing...
                        </>
                      ) : (
                        <>
                          {bulkDateValue ? '📅 ' : ''}
                          {`${bulkDateValue ? 'Change Date & ' : ''}Assign ${selectedJobs.length} ${selectedJobs.length === 1 ? 'job' : 'jobs'} to ${
                            assignmentType === 'user' ? 'Individual' : 'Team'
                          }`}
                        </>
                      )}
                    </button>

                    {/* Optional: Keep separate date-only button for flexibility */}
                    {bulkDateValue && !selectedUserId && !selectedTeamId && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleBulkDateUpdate();
                        }}
                        disabled={
                          selectedJobs.length === 0 || 
                          bulkDateUpdateLoading ||
                          !onBulkDateUpdate
                        }
                        className="px-4 py-2 bg-orange-600 text-white whitespace-nowrap rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {bulkDateUpdateLoading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Updating...
                          </>
                        ) : (
                          `📅 Change Date Only`
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* No eligible users warning */}
              {sortedUsers.length === 0 && userFilteringInfo && userFilteringInfo.totalCount > 0 && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <div className="flex items-start gap-2">
                    <svg className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <div className="text-sm">
                      <p className="font-medium text-yellow-800 mb-1">
                        No eligible team members found
                      </p>
                      <p className="text-yellow-700">
                        Try adjusting the area or service type filters to see more team members.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div 
                className="rounded-lg overflow-hidden h-[800px] overflow-y-auto" 
                ref={containerRef}
              >
                
                {/* Job count by date - show when we have multiple dates */}
                {hasMultipleDates && (
                  <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                    <div className="flex flex-wrap gap-2">
                      <div className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded-full">
                        Total Unassigned: {totalJobsCount.unassigned}
                      </div>
                      {Object.keys(jobsByDate).map(dateKey => (
                        <div key={dateKey} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full">
                          {safeFormatDate(dateKey, 'MMM d')}: {jobsByDate[dateKey].length} loaded
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* 🎯 ENHANCED: Show loading indicator when loading more jobs */}
                {renderLoadingState()}
                
                {/* Display message when no unassigned jobs are available */}
                {renderEmptyState()}
                
                {/* Jobs grouped by date */}
                <div className="divide-y divide-gray-200">
                  {Object.keys(jobsByDate).length > 0 ? (
                    (() => {
                      let globalIndex = 0; // Track index across all jobs
                      return Object.keys(jobsByDate).map(dateKey => (
                        <div key={dateKey} className="divide-y divide-gray-100">
                          {/* Date header */}
                          <div className="px-4 py-2 bg-gray-100 sticky top-0 z-10">
                            <h4 className="font-medium text-gray-800">
                              {safeFormatDate(dateKey)}
                            </h4>
                            <p className="text-xs text-gray-500">
                              {jobsByDate[dateKey].length} jobs loaded, {totalJobsCount.unassigned} total unassigned
                            </p>
                          </div>
                          
                          {/* Job cards */}
                          {jobsByDate[dateKey].map(job => (
                            <JobCardUnassigned
                              key={job.id}
                              job={job}
                              selectedJobs={selectedJobs}
                              setSelectedJobs={setSelectedJobs}
                              setEditingJob={setEditingJob}
                              index={globalIndex++}
                            />
                          ))}
                        </div>
                      ));
                    })()
                  ) : null}
                </div>
                
                {/* Load more button - show whenever more jobs are available, even if current jobs are assigned */}
                {!isLoadingMoreJobs && hasMoreJobs && (
                  <div className="p-4 border-t border-gray-200 flex justify-center">
                    <button
                      onClick={() => loadMoreJobs(15)}
                      className="px-5 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 font-medium shadow-sm transition-colors"
                    >
                      {Object.keys(jobsByDate).length > 0 
                        ? "Load 15 More Unassigned Jobs"
                        : "Load Next 15 Unassigned Jobs"
                      }
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Bulk Date Help Modal */}
      <Dialog open={showDateHelpModal} onClose={() => setShowDateHelpModal(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Bulk Date Change Help</span>
          <Button
            aria-label="Close help"
            onClick={() => setShowDateHelpModal(false)}
            tabIndex={0}
            sx={{ minWidth: 0, padding: 0, marginLeft: 1 }}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </Button>
        </DialogTitle>
        <DialogContent>
          <div className="prose max-w-none">
            <h3 className="text-lg font-semibold mb-3">How Bulk Date Changes Work</h3>
            
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">📋 Step-by-Step Process:</h4>
                <ol className="list-decimal list-inside space-y-2 text-gray-700">
                  <li><strong>Select Jobs:</strong> Use the checkboxes to select multiple jobs you want to modify</li>
                  <li><strong>Choose New Date:</strong> Pick a date from the date picker (optional)</li>
                  <li><strong>Choose Assignment:</strong> Select a team member or team to assign the jobs to</li>
                  <li><strong>Execute:</strong> Click the green button to apply both changes at once</li>
                </ol>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">🎯 What You Can Do:</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700">
                  <li><strong>Combined Operation (Recommended):</strong> Change dates AND assign in one operation - most efficient and safe</li>
                  <li><strong>Date Only:</strong> Change dates without assigning - jobs will be deselected after the change</li>
                  <li><strong>Assignment Only:</strong> Assign jobs without changing dates</li>
                </ul>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">⚡ Key Features:</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700">
                  <li><strong>Date Override:</strong> Sets permanent date override to prevent automatic changes</li>
                  <li><strong>Audit Trail:</strong> All changes are logged with your name and timestamp</li>
                  <li><strong>Transaction Safety:</strong> All jobs are updated together - if one fails, none are changed</li>
                  <li><strong>Smart Assignment:</strong> Automatically handles conflicts between user and team assignments</li>
                </ul>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <h4 className="font-medium text-blue-900 mb-2">💡 Pro Tips:</h4>
                <ul className="list-disc list-inside space-y-1 text-blue-800 text-sm">
                  <li>Use the combined operation for best efficiency - one click does everything</li>
                  <li>Job selections are cleared after date changes to prevent accidental assignments</li>
                  <li>Date overrides prevent future automatic date changes from reservations</li>
                  <li>All changes are immediately visible and can be seen in job history</li>
                </ul>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
});

export default UnassignedTasks; 